import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Space } from 'antd';
import type { MenuProps } from 'antd';
import { DownOutlined, HomeOutlined, MenuOutlined } from '@ant-design/icons';
import { UserMenu } from '@pages/cmp/nav/UserMenu.tsx';
import { CartMenu } from '@eshop/pages/cart/CartMenu.tsx';
import { useAppSelector } from '@store/store.tsx';
import { selectCmsEnabled } from '@store/slices/settingsSlice.tsx';
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import { useCheckoutLayout } from '@eshop/hooks/useCheckoutLayout.tsx';
import { CatalogueTemplateContext } from '@eshop/templates/TemplateContext';
import type { CatalogueLinkStyle } from '@eshop/templates/types';
import { getLegacyWebNavigation } from '@features/cms/legacy-web/api.ts';
import type { LegacyWebNavigationItem } from '@features/cms/legacy-web/types.ts';

interface Props {
  cartShape?: 'default' | 'circle' | 'round';
  cartIconColor?: string;
  className?: string;
  catalogueLinkStyle?: CatalogueLinkStyle;
  catalogueLinkText?: string;
}

const HOME_SLUGS = new Set(['index', 'home', 'inicio']);

function isHomeSection(item: LegacyWebNavigationItem): boolean {
  const slug = item.slug?.trim().toLowerCase();
  if (slug && HOME_SLUGS.has(slug)) return true;
  const href = item.href?.trim().toLowerCase();
  if (href === '/web/#home' || href === '/web/home' || href === '/web/' || href === '/web') return true;
  return false;
}

function filterWebNavigation(items: LegacyWebNavigationItem[]): LegacyWebNavigationItem[] {
  return items
    .filter((item) => !isHomeSection(item))
    .map((item) => ({
      ...item,
      children: item.children ? filterWebNavigation(item.children) : [],
    }));
}

function normalizeNavigationHref(item: LegacyWebNavigationItem): string {
  const href = item.href?.trim();
  if (!href) {
    return item.slug ? `/web/#${item.slug.replace(/^#/, '')}` : '/web/#home';
  }

  if (/^(?:https?:|mailto:|tel:|\/\/)/i.test(href)) {
    return href;
  }

  const isLegacyWebHref = href === '/web' || href === '/web/' || href.startsWith('/web/');
  const isHashHref = href.startsWith('#');
  if (!isLegacyWebHref && !isHashHref) {
    return href;
  }

  const hashIndex = href.indexOf('#');
  const slug = (hashIndex >= 0 ? href.slice(hashIndex + 1) : item.slug ?? '').replace(/^#/, '');
  return slug ? `/web/#${slug}` : '/web/#home';
}

function navigationMenuItems(items: LegacyWebNavigationItem[]): MenuProps['items'] {
  return items.map((item) => ({
    key: String(item.id),
    label: <a href={normalizeNavigationHref(item)}>{item.label}</a>,
    children: item.children.length
      ? navigationMenuItems(item.children)
      : undefined,
  }));
}

const ShopNavBar: React.FC<Props> = ({
  cartShape,
  cartIconColor,
  className,
  catalogueLinkStyle: propLinkStyle,
  catalogueLinkText: propLinkText,
}) => {
  const { resolvedTheme } = useThemeMode();
  const isStaff = useAppSelector((state) => state.user.isStaff);
  const cmsEnabled = useAppSelector(selectCmsEnabled);
  const { hideNavigation } = useCheckoutLayout();
  const suppressUserMenu = hideNavigation && !isStaff;
  const templateContext = useContext(CatalogueTemplateContext);
  const [webNavigation, setWebNavigation] = useState<LegacyWebNavigationItem[] | null>(null);

  useEffect(() => {
    if (!cmsEnabled) {
      setWebNavigation(null);
      return;
    }
    let cancelled = false;
    getLegacyWebNavigation()
      .then((response) => {
        if (!cancelled) setWebNavigation(response.items);
      })
      .catch(() => {
        if (!cancelled) setWebNavigation(null);
      });
    return () => {
      cancelled = true;
    };
  }, [cmsEnabled]);

  const linkStyle: CatalogueLinkStyle =
    propLinkStyle ?? templateContext?.catalogueLinkStyle ?? 'text';
  const linkText = propLinkText ?? templateContext?.catalogueLinkText ?? 'Catálogo';
  const integrated = cmsEnabled && webNavigation !== null;

  const filteredWebNavigation = useMemo(
    () => (webNavigation ? filterWebNavigation(webNavigation) : []),
    [webNavigation]
  );

  const mobileItems = useMemo<MenuProps['items']>(() => {
    if (!integrated) {
      return [{ key: 'catalogue', label: <a href="/eshop/catalogue">{linkText}</a> }];
    }
    return [
      ...(navigationMenuItems(filteredWebNavigation) ?? []),
      { key: 'catalogue', label: <a href="/eshop/catalogue">Catálogo</a> },
    ];
  }, [integrated, linkText, filteredWebNavigation]);

  const catalogueLink = (
    <a
      href="/eshop/catalogue"
      className={`shop-navbar__link ${linkStyle === 'icon' ? 'shop-navbar__home' : 'fw-semibold'}`}
      aria-label="Ir al catálogo"
    >
      {(linkStyle === 'icon' || linkStyle === 'both') && <HomeOutlined />}
      {linkStyle !== 'icon' ? linkText : null}
    </a>
  );

  return (
    <nav
      className={`shop-navbar d-flex align-items-center gap-3 theme-${resolvedTheme} ${className ?? ''}`}
      data-theme={resolvedTheme}
      aria-label="Navegación de la tienda"
    >
      {!hideNavigation ? (
        <>
          <div className="shop-navbar__desktop-links">
            {integrated ? (
              <Space size={16}>
                {filteredWebNavigation.map((item) => (
                  item.children.length ? (
                    <Dropdown
                      key={item.id}
                      menu={{ items: navigationMenuItems(item.children) }}
                      trigger={['hover', 'click']}
                    >
                      <a href={normalizeNavigationHref(item)} className="shop-navbar__link">
                        {item.label} <DownOutlined aria-hidden />
                      </a>
                    </Dropdown>
                  ) : (
                    <a key={item.id} href={normalizeNavigationHref(item)} className="shop-navbar__link">
                      {item.label}
                    </a>
                  )
                ))}
                <a href="/eshop/catalogue" className="shop-navbar__link is-active">
                  Catálogo
                </a>
              </Space>
            ) : catalogueLink}
          </div>
          <Dropdown menu={{ items: mobileItems }} trigger={['click']}>
            <Button
              type="text"
              className="shop-navbar__mobile-trigger"
              icon={<MenuOutlined />}
              aria-label="Abrir menú de navegación"
            />
          </Dropdown>
        </>
      ) : null}
      <CartMenu shape={cartShape} iconColor={cartIconColor} hidden={hideNavigation} />
      {!suppressUserMenu ? <UserMenu /> : null}
    </nav>
  );
};

export default ShopNavBar;
